<pin-page [backLink]="true" backLinkText="Back to Manage Access" [backLinkUrl]="internalRoutes.MANAGE_ACCESS">
  <pin-page-title [type]="PageTitleType.H1" id="main-content" tabIndex="-1" role="complementary" aria-label="share-access-title">
    Share Access
  </pin-page-title>

  <pin-page-content role="main">

    @if (accountAlreadyShared) {
      <pin-notification type="alert">
        <div>You have already shared one or more of these accounts with this email address.</div>
      </pin-notification>
    }

    @if (displayEmailNotValidError) {
      <pin-notification type="alert">
        <div>There are multiple DCP users registered with this email address. To share with them, please contact your
          institution.
        </div>
      </pin-notification>
    }

    @if (invitationDenied) {
      <pin-notification type="alert">
        <div>Unable to share with this email address. Please contact your institution.</div>
      </pin-notification>
    }

    @if (tryAgainLater) {
      <pin-notification type="alert">
        <div>This page is currently unavailable. Please try again later.</div>
      </pin-notification>
    }

    <pin-notification type="warning">
      <div class="flex flex-col gap-2">
        Share access to one or more accounts. Please note:
        <ul class="list-disc list-outside ms-8">
          <li>The person you are sharing your account(s) with will be able to reject placements at Destination
            Institutions during Depositor Placement Review.
          </li>
          @if (showStatements) {
            <li>Consolidated statements, which contain account information for multiple accounts, will be available to
              anyone who can view any of the accounts included on the statement.
            </li>
          }
        </ul>
      </div>
    </pin-notification>

    <form [formGroup]="shareAccessForm" (ngSubmit)="confirmAndShareAccess()">
      <pin-card>
        <pin-card-title customClass="dcp-h2">
          Enter Email Address
        </pin-card-title>

        <pin-card-content>
          <p class="dcp-body">
            Enter the email address of the person with whom you want to share access.
          </p>

          <div>
            <mat-form-field appearance="outline" id="email" class="w-2/5">
              <mat-label>Non-Account Holder Email Address</mat-label>
              <input matInput
                     formControlName="email"
                     placeholder="Non-Account Holder Email Address"
                     maxlength="50">

              @if (shareAccessForm.controls['email'].hasError('required')) {
                <mat-error id="email.required">
                  Email is required
                </mat-error>
              }
              @if (shareAccessForm.controls['email'].hasError('email') || shareAccessForm.controls['email'].hasError('pattern')) {
                <mat-error id="email.invalid">
                  Email is invalid
                </mat-error>
              }
            </mat-form-field>
          </div>

          <div class="flex flex-col gap-6">
            <div class="dcp-h2"> Select Accounts To Share</div>
            @if (hasCdarsAccounts) {
              <pin-notification type="info">
                <div>The ability to share DCP access for CDARS accounts is not currently available. We are working to
                  make this feature available soon.
                </div>
              </pin-notification>
            }
            <div class="flex flex-row justify-center gap-5">
              <div class="flex flex-col justify-start w-1/2">
                <mat-form-field id="accountOwnerDropdownPlaceholder" appearance="outline">
                  <mat-label>Depositor</mat-label>

                  <mat-select formControlName="accountOwner"
                              disableOptionCentering panelClass="matSelectPanel"
                              (selectionChange)="loadAccountsForAccountOwner($event.value)">
                    @for (accountOwner of accountOwnerList; track accountOwner) {
                      <mat-option [value]="accountOwner">
                        {{ accountOwner.name }}
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </div>

              <div class="flex flex-col justify-end w-1/2">
                <mat-form-field appearance="outline">
                  <mat-label id="accountDropdownPlaceholder">
                    @if (selectedAccountOwnerId) {
                      Account Title and Institution Transaction Account Number
                    } @else {
                      Please select a Depositor first
                    }
                  </mat-label>

                  <mat-select formControlName="account"
                              disableOptionCentering panelClass="matSelectPanel"
                              (selectionChange)="selectAccounts($event.value)">
                    @if (matchingAccounts.length === 0) {
                      <mat-option disabled></mat-option>
                    }
                    @if (matchingAccounts.length > 1) {
                      <mat-option [value]="matchingAccounts">Select All</mat-option>
                    }
                    @for (account of matchingAccounts; track account) {
                      <mat-option [value]="account">
                        {{ account.title }}
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </div>
            </div>
          </div>

          <pin-generic-data-grid name="ShareAccess"
                                 [data]="selectedAccounts"
                                 [columnDefinitions]="columnDefinitions"
                                 [displayedColumns]="displayedColumns"
                                 [columnSizes]="[80, 20]"
                                 [showExport]="false"
                                 [showPrint]="false"
                                 initialSortColumn="title"
                                 initialSortDirection="asc"
                                 [showFilter]="false"
                                 [hidden]="selectedAccounts?.length === 0">
          </pin-generic-data-grid>
        </pin-card-content>

        <pin-card-actions>
          <div class="flex flex-row justify-end items-center mt-4">
            <pin-button buttonStyle="filled" buttonId="confirm-and-share-access" [isDisabled]="!isDataValid() || processingRequest">CONFIRM AND SHARE ACCESS</pin-button>
          </div>
        </pin-card-actions>
      </pin-card>
    </form>
  </pin-page-content>
</pin-page>

@if (processingRequest) {
  <div class="pin-spinner fullscreen">
    <mat-progress-spinner mode="indeterminate"></mat-progress-spinner>
  </div>
}
